import {
  Row,
  Col,
  Card,
  Table,
  message,
  Button,
  Modal,
  Input,
  Flex,
} from "antd";

import { PlusOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";

import { useOrganizationData } from "features/administration/contexts/OrgsDataProvider";
import {
  addOrganizationMembers,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  updateOrganizationMemberRoles,
  deleteOrganizationMember,
  addOrganizationRobot,
} from "shared/services/organizations_services";
import { useAuth } from "shared/contexts/AuthProvider";
import { servicesUrlConfig } from "shared/config/setting";
import { BUTTON_STYLES } from "../constants";
import {
  createOrgsColumns,
  createOrgMembersColumns,
  createOrgRobotColumns,
} from "../components/tableColumns";
import {
  AddOrganizationModal,
  EditOrganizationModal,
} from "../components/OrganizationModals";
import { AddMemberModal, EditMemberModal } from "../components/MemberModals";
import { useTranslation } from "react-i18next";
import logger from "shared/utils/logging";

const OrgsManagement = () => {
  const { t, ready } = useTranslation();

  const translate = (key) => {
    return ready ? t(key) : key;
  };

  const {
    organizationsData,
    getOrganizationsList,
    dataLoading,
    getOrganizationsDetail,
  } = useOrganizationData();

  const { auth, authFetch, isSuperUser } = useAuth();

  useEffect(() => {
    getOrganizationsList();
  }, []);

  const organizations = organizationsData.data;
  const currentOrgDetail = organizationsData.currentOrgDetail;
  const memberData = currentOrgDetail?.members;
  const robotsData = currentOrgDetail?.robots;
  const { page, per_page, total, results } = organizations || {
    page: 1,
    per_page: 10,
    total: 0,
    results: [],
  };

  // const [selectedOrgMembers, setSelectedOrgMembers] = useState([]);
  // const [selectedOrgRobots, setSelectedOrgRobots] = useState([]);
  // const [selectedOrgId, setSelectedOrgId] = useState(null);

  const selectedOrgId = currentOrgDetail?.org_id || null;
  const selectedOrgMembers = currentOrgDetail?.members?.results || [];
  const selectedOrgRobots = currentOrgDetail?.robots?.results || [];

  const handleRowSelection = (record) => {
    logger.log("Selected record: ", record);
    getOrganizationsDetail(record.org_id);
    // setIsOrgDetailModalVisible(true);
  };

  const [isMemberModalVisible, setIsMemberModalVisible] = useState(false);
  const [isRobotModalVisible, setIsRobotModalVisible] = useState(false);
  const [newMember, setNewMember] = useState({ username: "", roles: [] });
  const [newRobot, setNewRobot] = useState({ robot_id: "" });
  const [isEditMemberModalVisible, setIsEditMemberModalVisible] =
    useState(false);
  const [editingMember, setEditingMember] = useState({
    username: "",
    roles: [],
  });
  const [isOrgModalVisible, setIsOrgModalVisible] = useState(false);
  const [isEditOrgModalVisible, setIsEditOrgModalVisible] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: "", description: "" });
  const [editingOrg, setEditingOrg] = useState({
    org_id: null,
    name: "",
    description: "",
  });
  const [isOrgDetailModalVisible, setIsOrgDetailModalVisible] = useState(false);

  const handleAddMember = () => {
    logger.log("Adding member:", newMember);
    if (selectedOrgId === null) {
      message.error("Please select an organization first.");
      return;
    }
    if (!newMember.username || newMember.roles.length === 0) {
      message.error("Please enter a username and select at least one role.");
      return;
    }

    addOrganizationMembers(
      auth.token,
      selectedOrgId,
      newMember.username,
      newMember.roles,
    )
      .then((response) => {
        logger.log("Member added successfully:", response);
        message.success("Member added successfully.");
        setIsMemberModalVisible(false);
        setNewMember({ username: "", roles: [] });
        getOrganizationsDetail(selectedOrgId); // Refresh the organization details
      })
      .catch((error) => {
        logger.error("Error adding member:", error);
        message.error(error?.message || "Failed to add member.");
      });
  };

  const handleUpdateMemberRoles = () => {
    logger.log("Updating member roles:", editingMember);
    if (selectedOrgId === null) {
      message.error("Please select an organization first.");
      return;
    }
    if (!editingMember.username) {
      message.error("Please select member first.");
      return;
    }

    updateOrganizationMemberRoles(
      auth.token,
      selectedOrgId,
      editingMember.username,
      editingMember.roles,
    )
      .then((response) => {
        logger.log("Member roles updated successfully:", response);
        message.success("Member roles updated successfully.");
        setIsEditMemberModalVisible(false);
        setEditingMember({ username: "", roles: [] });
        getOrganizationsDetail(selectedOrgId); // Refresh the organization details
      })
      .catch((error) => {
        logger.error("Error updating member roles:", error);
        message.error(error?.message || "Failed to update member roles.");
      });
  };

  const handleAddOrganization = () => {
    logger.log("Adding organization:", newOrg);
    if (!newOrg.name || !newOrg.description) {
      message.error("Please enter organization name and description.");
      return;
    }

    createOrganization(auth.token, newOrg.name, newOrg.description)
      .then((response) => {
        logger.log("Organization added successfully:", response);
        message.success("Organization added successfully.");
        setIsOrgModalVisible(false);
        setNewOrg({ name: "", description: "" });
        getOrganizationsList(); // Refresh the organizations list
      })
      .catch((error) => {
        logger.error("Error adding organization:", error);
        message.error(error?.message || "Failed to add organization.");
      });
  };

  const handleUpdateOrganization = () => {
    logger.log("Updating organization:", editingOrg);
    if (!editingOrg.org_id || !editingOrg.name || !editingOrg.description) {
      message.error("Please select an organization and provide all details.");
      return;
    }

    updateOrganization(
      auth.token,
      editingOrg.org_id,
      editingOrg.name,
      editingOrg.description,
    )
      .then((response) => {
        logger.log("Organization updated successfully:", response);
        message.success("Organization updated successfully.");
        setIsEditOrgModalVisible(false);
        setEditingOrg({ org_id: null, name: "", description: "" });
        getOrganizationsList(); // Refresh the organizations list
      })
      .catch((error) => {
        logger.error("Error updating organization:", error);
        message.error(error?.message || "Failed to update organization.");
      });
  };

  const handleDeleteOrganization = (org) => {
    const org_id = org.org_id;
    logger.log("Deleting organization:", org_id, org.name);
    Modal.confirm({
      title: "Are you sure you want to delete this organization?",
      content: `Organization name: ${org.name}`,
      okText: "Yes",
      cancelText: "No",
      onOk: () => {
        deleteOrganization(auth.token, org_id)
          .then((response) => {
            logger.log("Organization deleted successfully:", response);
            message.success("Organization deleted successfully.");
            getOrganizationsList(); // Refresh the organizations list
          })
          .catch((error) => {
            logger.error("Error deleting organization:", error);
            message.error(error?.message || "Failed to delete organization.");
          });
      },
    });
  };

  const handleAddRobot = () => {
    logger.log("Adding robot:", newRobot);
    if (selectedOrgId === null) {
      message.error("Please select an organization first.");
      return;
    }
    if (!newRobot.robot_id) {
      message.error("Please enter robot id.");
      return;
    }

    addOrganizationRobot(auth.token, selectedOrgId, newRobot.robot_id)
      .then((response) => {
        logger.log("Robot added successfully:", response);
        message.success("Robot added successfully.");
        setIsRobotModalVisible(false);
        setNewRobot({ robot_id: "" });
        getOrganizationsDetail(selectedOrgId); // Refresh the organization details
      })
      .catch((error) => {
        logger.error("Error adding robot:", error);
        message.error(error?.message || "Failed to add robot.");
      });
  };

  const handleRemoveMember = (username) => {
    logger.log("Removing member:", username);
    if (selectedOrgId === null) {
      message.error("Please select an organization first.");
      return;
    }

    Modal.confirm({
      title: "Are you sure you want to remove this member?",
      content: `Username: ${username}`,
      okText: "Yes",
      cancelText: "No",
      onOk: () => {
        deleteOrganizationMember(auth.token, selectedOrgId, username)
          .then((response) => {
            logger.log("Member removed successfully:", response);
            message.success("Member removed successfully.");
            getOrganizationsDetail(selectedOrgId); // Refresh the organization details
          })
          .catch((error) => {
            logger.error("Error removing member:", error);
            message.error(error?.message || "Failed to remove member.");
          });
      },
    });
  };

  const handleRemoveRobot = (robotId) => {
    logger.log("Removing robot:", robotId);
    if (selectedOrgId === null) {
      message.error("Please select an organization first.");
      return;
    }

    Modal.confirm({
      title: "Are you sure you want to remove this robot?",
      content: `Robot ID: ${robotId}`,
      okText: "Yes",
      cancelText: "No",
      onOk: () => {
        const url = `${servicesUrlConfig.ORGS_SERVICE_URL}/${selectedOrgId}/robots?robot_id=${robotId}`;
        authFetch(url, {
          method: "DELETE",
          headers: {
            accept: "*/*",
            Authorization: `Bearer ${auth.token}`,
          },
        })
          .then((response) => {
            logger.log("Robot removed successfully:", response);
            message.success("Robot removed successfully.");
            getOrganizationsDetail(selectedOrgId);
          })
          .catch((error) => {
            logger.error("Error removing robot:", error);
            message.error(
              error.response?.data?.message ||
                error.message ||
                "Failed to remove robot.",
            );
          });
      },
    });
  };

  const handleEditMember = (record) => {
    setEditingMember({ username: record?.user?.username, roles: record.roles });
    setIsEditMemberModalVisible(true);
  };

  const handleEditOrganization = (record) => {
    setEditingOrg({
      org_id: record.org_id,
      name: record.name,
      description: record.description,
    });
    setIsEditOrgModalVisible(true);
  };

  // Create table columns using shared factories
  const orgsColumns = createOrgsColumns(translate, {
    isMyOrgs: false,
    showActions: true,
    onEdit: handleEditOrganization,
    onDelete: handleDeleteOrganization,
  });

  const orgMembersColumns = createOrgMembersColumns(translate, {
    showActions: true,
    onEdit: handleEditMember,
    onDelete: handleRemoveMember,
  });

  const orgRobotColumns = createOrgRobotColumns(translate, {
    showActions: true,
    onDelete: handleRemoveRobot,
  });

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              variant="borderless"
              className="criclebox tablespace mb-24"
              title="Organizations"
              extra={
                <Button
                  style={BUTTON_STYLES.add}
                  icon={<PlusOutlined />}
                  onClick={() => setIsOrgModalVisible(true)}
                >
                  New Organization
                </Button>
              }
            >
              <div className="table-responsive">
                <Table
                  columns={[
                    ...orgsColumns,
                    {
                      // title: "Action",
                      key: "action",
                      render: (_, record) => (
                        <>
                          <Button
                            type="link"
                            onClick={() => handleEditOrganization(record)}
                          >
                            Edit
                          </Button>
                          <Button
                            type="link"
                            danger
                            onClick={() => handleDeleteOrganization(record)}
                          >
                            Delete
                          </Button>
                        </>
                      ),
                    },
                  ]}
                  dataSource={results}
                  rowKey="org_id"
                  rowClassName={(record) =>
                    record.org_id === selectedOrgId ? "selected-row" : ""
                  }
                  onRow={(record) => ({
                    onClick: () => handleRowSelection(record),
                  })}
                  className="ant-border-space"
                />
              </div>
            </Card>
          </Col>
        </Row>
        <Row gutter={[24, 0]}>
          <Col xs={24} sm={24} md={12} lg={12} xl={14} className="mb-24">
            <Card
              variant="borderless"
              className="criclebox tablespace mb-24"
              title="Organization Members"
              extra={
                <Button
                  icon={<PlusOutlined />}
                  style={BUTTON_STYLES.add}
                  onClick={() => setIsMemberModalVisible(true)}
                >
                  Add Member
                </Button>
              }
            >
              <div className="table-responsive">
                <Table
                  columns={[
                    ...orgMembersColumns,
                    // {
                    //   // title: "Action",
                    //   key: "action",
                    //   align: "center",
                    //   render: (_, record) => (
                    //     <Flex justify="space-between" align="center">
                    //       <Button
                    //         type="link"
                    //         onClick={() => handleEditMember(record)}
                    //       >
                    //         Edit
                    //       </Button>
                    //       <Button
                    //         type="link"
                    //         danger
                    //         onClick={() =>
                    //           handleRemoveMember(record?.user?.username)
                    //         }
                    //       >
                    //         Remove
                    //       </Button>
                    //     </Flex>
                    //   ),
                    // },
                  ]}
                  dataSource={selectedOrgMembers}
                  pagination={false}
                  rowKey={(record) => record.user.username}
                  className="ant-border-space"
                />
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={10} className="mb-24">
            <Card
              variant="borderless"
              className="criclebox tablespace mb-24"
              title="Organization Robots"
              extra={
                <Button
                  style={BUTTON_STYLES.add}
                  icon={<PlusOutlined />}
                  onClick={() => setIsRobotModalVisible(true)}
                >
                  Add Robot
                </Button>
              }
            >
              <div className="table-responsive">
                <Table
                  columns={[
                    ...orgRobotColumns,
                    {
                      // title: "Action",
                      key: "action",
                      render: (_, record) => (
                        <Button
                          type="link"
                          danger
                          onClick={() => handleRemoveRobot(record.robot_id)}
                        >
                          Remove
                        </Button>
                      ),
                    },
                  ]}
                  dataSource={selectedOrgRobots}
                  rowKey="robot_id"
                  pagination={false}
                  className="ant-border-space"
                />
              </div>
            </Card>
            {/* </Card> */}
          </Col>
        </Row>
      </div>

      {/* Add Member Modal */}
      <AddMemberModal
        visible={isMemberModalVisible}
        member={newMember}
        onOk={handleAddMember}
        onCancel={() => setIsMemberModalVisible(false)}
        onChange={(updates) => setNewMember({ ...newMember, ...updates })}
        translate={translate}
      />

      {/* Edit Member Modal */}
      <EditMemberModal
        visible={isEditMemberModalVisible}
        member={editingMember}
        onOk={handleUpdateMemberRoles}
        onCancel={() => setIsEditMemberModalVisible(false)}
        onChange={(updates) =>
          setEditingMember({ ...editingMember, ...updates })
        }
        translate={translate}
      />

      {/* Add Robot Modal */}
      <Modal
        title="Add Robot"
        open={isRobotModalVisible}
        onOk={handleAddRobot}
        onCancel={() => setIsRobotModalVisible(false)}
      >
        <Input
          placeholder="Robot ID"
          value={newRobot.robot_id}
          onChange={(e) =>
            setNewRobot({ ...newRobot, robot_id: e.target.value })
          }
        />
      </Modal>

      {/* Add Organization Modal */}
      <AddOrganizationModal
        visible={isOrgModalVisible}
        organization={newOrg}
        onOk={handleAddOrganization}
        onCancel={() => setIsOrgModalVisible(false)}
        onChange={(updates) => setNewOrg({ ...newOrg, ...updates })}
        translate={translate}
      />

      {/* Edit Organization Modal */}
      <EditOrganizationModal
        visible={isEditOrgModalVisible}
        organization={editingOrg}
        onOk={handleUpdateOrganization}
        onCancel={() => setIsEditOrgModalVisible(false)}
        onChange={(updates) => setEditingOrg({ ...editingOrg, ...updates })}
        canManageStorage={isSuperUser}
        translate={translate}
      />

      {/* Organization Details Modal - Commented Out */}
    </>
  );
};

// Add CSS for highlighting the selected row
const styles = `
  .selected-row {
    background-color: #e6f7ff !important;
  }
`;
document.head.insertAdjacentHTML("beforeend", `<style>${styles}</style>`);

export default OrgsManagement;
